import { User } from '../models';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { JwtService } from './jwt.service';
import { ApiService } from './api.service';
import { ApiV2AuthenticationService } from './api-v2-authentication.service';
import { Injectable } from '@angular/core';
import { DataTableParams } from 'angular-datatables-all-versions';
import { saveAs } from 'file-saver';

@Injectable()
export class StudentsForImpactService {

  private currentUserSubject = new BehaviorSubject<User>(new User());
  public currentUser = this.currentUserSubject.asObservable().distinctUntilChanged();

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();


  constructor(private apiService: ApiService,
    private apiV2AuthService: ApiV2AuthenticationService
  ) { }


  findImpact(params: any, useV2: boolean = false): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.get('/users/impact/filter', params, useV2, token)
      ),
      map((response: any) => response || {})
    )
  }

  listImpact(useV2: boolean = false): Observable<User[]> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.get('/users/impact', undefined, useV2, token)
      ),
      map((response: any) => response.users || [])
    )
  }

  validateImpactDocuments(userId: number, validationData: any): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.post(`/users/impact-validation/${userId}`, validationData, true, token)
      ),
      map((response: any) => response || {})
    )
  }

  findByIdAndFetchDetails(userId: number, useV2: boolean = false): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.get(`/users/${userId}/details`, undefined, useV2, token)
      ),
      map((response: any) => response.data || {})
    )
  }

  getUserStats(userId: number, useV2: boolean = false): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.get(`/users/${userId}/stats`, undefined, useV2, token)
      ),
      map((response: any) => response.stats || {})
    )
  }

  getDegreeCategoriesFilters(useV2: boolean = false): Observable<any> {
    return this.apiService.get(`/users/degree/categories`, undefined, useV2).pipe(
          map((response: any) => response.data || [])
    );
  }

  getDegreeYearsFilters(useV2: boolean = false): Observable<any> {
    return this.apiService.get(`/users/degree/years`, undefined, useV2).pipe(
          map((response: any) => response.data || [])
    );
  }

  /**
   * Sauvegarde le profil utilisateur (details/motivation)
   */
  saveProfile(userId:number, details: string): Observable<any> {
    const formData = new FormData();
    formData.append('details', details || '');

    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token => this.apiService.postFormData(`/users/${userId}/profile`, formData, true, token))
    );
  }

  /**
   * Sauvegarde les informations d'études
   */
  saveDegree(userId:number, institution: string, degreeCategoryId: number, degreeYearId: number, validity?: string): Observable<any> {
    const formData = new FormData();
    formData.append('institution', institution);
    formData.append('degree_category_id', degreeCategoryId.toString());
    formData.append('degree_year_id', degreeYearId.toString());
    
    if (validity) {
      formData.append('validity', validity);
    }

    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token => this.apiService.postFormData(`/users/${userId}/degree`, formData, true, token))
    );
  }

  /**
   * Sauvegarde les informations d'indépendant
   */
  saveSelfEmployed(userId:number, siret: string): Observable<any> {
    const formData = new FormData();
    formData.append('siret', siret);

    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token => this.apiService.postFormData(`/users/${userId}/self-employed`, formData, true, token))
    );
  }

  validateSiret(siret: string): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.get(`/insee/siret/${siret}/impact`, undefined, true, token)
      ),
      map((response: any) => response.data || {})
    )
  }

  onActivateOrDeactivateCertificationImpact(userId:number): Observable<any> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.post(`/users/certificate/${userId}`, undefined, true, token)
      ),
      map((response: any) => response.data || {})
    )
  }


  /**
   * Exporte les données des utilisateurs impact
   * @returns Observable du résultat du téléchargement
   */
  exportImpactUser(): Observable<{ success: boolean; error?: string }> {
    return this.apiV2AuthService.getApiV2Token().pipe(
      switchMap(token =>
        this.apiService.getText(
          '/users/impact/export',
          true,
          token
        )
      ),
      map((response: any) => {
        if (!response.body || response.status !== 200) {
          return {
            success: false,
            error: 'Erreur lors de l\'export des utilisateurs impact',
          };
        }

        // Télécharger le fichier (response.body est un ArrayBuffer)
        
        // Essayer d'extraire le nom du fichier depuis Content-Disposition
        let fileName = this.generateExportFileName();
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*?)\1(?:;|$)/i);
          if (filenameMatch && filenameMatch[2]) {
            fileName = filenameMatch[2];
          }
        }
        
        // Créer le blob et utiliser file-saver pour le téléchargement
        const blob = new Blob([response.body], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, fileName);

        return { success: true };
      }),
      catchError(error => {
        console.error('Erreur export utilisateurs impact:', error);
        return of({
          success: false,
          error: error instanceof Error ? error.message : 'Erreur réseau',
        });
      })
    );
  }

  private generateExportFileName(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = ('0' + (now.getMonth() + 1)).slice(-2);
    const day = ('0' + now.getDate()).slice(-2);
    const hours = ('0' + now.getHours()).slice(-2);
    const minutes = ('0' + now.getMinutes()).slice(-2);
    const seconds = ('0' + now.getSeconds()).slice(-2);
    return `students_for_impact_export_${year}_${month}_${day}_${hours}${minutes}${seconds}.csv`;
  }
}